import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    return response.data;
  },
  
  forgotPassword: async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { 
      email,
      origin: window.location.origin 
    });
    return response.data;
  },
  
  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  },

  
  getMe: async () => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Orders API
export const ordersAPI = {
  getOrders: async () => {
    const response = await axios.get(`${API}/orders/`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  getOrder: async (orderId) => {
    const response = await axios.get(`${API}/orders/${orderId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  createOrder: async (orderData) => {
    const response = await axios.post(`${API}/orders/`, orderData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  updateOrderStatus: async (orderId, statusData) => {
    const response = await axios.patch(`${API}/orders/${orderId}/status`, statusData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  uploadOriginalFile: async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/orders/${orderId}/original-file`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  uploadModifiedFile: async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/orders/${orderId}/modified-file`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  downloadFile: async (orderId, fileType) => {
    const response = await axios.get(`${API}/orders/${orderId}/download/${fileType}`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${orderId}_${fileType}.bin`; // Fallback name
    
    if (contentDisposition) {
      // Try to match filename with quotes: filename="name.bin"
      let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (!filenameMatch) {
        // Try without quotes: filename=name.bin
        filenameMatch = contentDisposition.match(/filename=([^\s;]+)/);
      }
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    // Create download link — force binary type to preserve original format (.bin, .hex, etc.)
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return true;
  },

  // Log management
  getOrderLogs: async (orderId) => {
    const response = await axios.get(`${API}/orders/${orderId}/logs`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  uploadLog: async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/orders/${orderId}/logs/upload`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  addLogLink: async (orderId, url) => {
    const response = await axios.post(`${API}/orders/${orderId}/logs/link`, { url }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  openSAV: async (orderId, message = '') => {
    const response = await axios.post(`${API}/orders/${orderId}/sav`, { message }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  downloadLog: async (orderId, logId) => {
    const response = await axios.get(`${API}/orders/${orderId}/logs/${logId}/download`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `log_${logId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Use the blob type from response or fallback
    const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up the URL
    window.URL.revokeObjectURL(url);
  },

  renameLog: async (orderId, logId, customName) => {
    const response = await axios.patch(`${API}/orders/${orderId}/logs/${logId}/rename`, 
      { custom_name: customName }, 
      {
        headers: getAuthHeaders()
      }
    );
    return response.data;
  }
};

// Chat API
export const chatAPI = {
  getMessages: async (orderId) => {
    const response = await axios.get(`${API}/orders/${orderId}/messages`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  sendMessage: async (orderId, message) => {
    const response = await axios.post(`${API}/orders/${orderId}/messages`, { message }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  pollMessages: async (orderId, since) => {
    const params = since ? { since } : {};
    const response = await axios.get(`${API}/orders/${orderId}/messages/poll`, {
      headers: getAuthHeaders(),
      params
    });
    return response.data;
  }
};

// Payment API
export const paymentAPI = {
  createCheckoutSession: async (orderId) => {
    const response = await axios.post(`${API}/payment/create-checkout-session`, { 
      order_id: orderId,
      origin: window.location.origin 
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  createPaymentIntent: async (orderId) => {
    const response = await axios.post(`${API}/payment/create-intent`, { order_id: orderId }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },
  
  confirmPayment: async (paymentIntentId, orderId) => {
    const response = await axios.post(`${API}/payment/confirm`, {
      payment_intent_id: paymentIntentId,
      order_id: orderId
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Consulting API
export const consultingAPI = {
  createConsulting: async (consultingData) => {
    const response = await axios.post(`${API}/consulting/`, consultingData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getConsultings: async () => {
    const response = await axios.get(`${API}/consulting/`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getConsulting: async (consultingId) => {
    const response = await axios.get(`${API}/consulting/${consultingId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  updateConsultingStatus: async (consultingId, statusData) => {
    const response = await axios.patch(`${API}/consulting/${consultingId}/status`, statusData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  uploadFile: async (consultingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/consulting/${consultingId}/files/upload`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  addLink: async (consultingId, url) => {
    const response = await axios.post(`${API}/consulting/${consultingId}/files/link`, { url }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  downloadFile: async (consultingId, fileId) => {
    const response = await axios.get(`${API}/consulting/${consultingId}/files/${fileId}/download`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    return response.data;
  },

  getMessages: async (consultingId) => {
    const response = await axios.get(`${API}/consulting/${consultingId}/messages`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  sendMessage: async (consultingId, message) => {
    const response = await axios.post(`${API}/consulting/${consultingId}/messages`, { message }, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  createCheckoutSession: async (consultingId) => {
    const response = await axios.post(`${API}/payment/create-checkout-session-consulting`, 
      { 
        consulting_id: consultingId,
        origin: window.location.origin 
      }, 
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};

// Admin Consulting Price API
export const adminConsultingAPI = {
  getPrice: async () => {
    const response = await axios.get(`${API}/admin/consulting-price`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  updatePrice: async (price) => {
    const response = await axios.patch(`${API}/admin/consulting-price`, { price }, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};

// Super Admin API
export const superAdminAPI = {
  verifyToken: async (token) => {
    const response = await axios.get(`${API}/superadmin/verify-token`, {
      params: { token }
    });
    return response.data;
  },

  getStats: async (token) => {
    const response = await axios.get(`${API}/superadmin/stats`, {
      headers: { 'X-Super-Admin-Token': token }
    });
    return response.data;
  },

  getUsers: async (token, role = null) => {
    const response = await axios.get(`${API}/superadmin/users`, {
      headers: { 'X-Super-Admin-Token': token },
      params: role ? { role } : {}
    });
    return response.data;
  },

  deleteUser: async (token, userId) => {
    const response = await axios.delete(`${API}/superadmin/users/${userId}`, {
      headers: { 'X-Super-Admin-Token': token }
    });
    return response.data;
  },

  getOrders: async (token, status = null, limit = 100) => {
    const response = await axios.get(`${API}/superadmin/orders`, {
      headers: { 'X-Super-Admin-Token': token },
      params: { status, limit }
    });
    return response.data;
  },

  getConsultings: async (token, status = null, limit = 100) => {
    const response = await axios.get(`${API}/superadmin/consultings`, {
      headers: { 'X-Super-Admin-Token': token },
      params: { status, limit }
    });
    return response.data;
  },

  deleteOrder: async (token, orderId) => {
    const response = await axios.delete(`${API}/superadmin/orders/${orderId}`, {
      headers: { 'X-Super-Admin-Token': token }
    });
    return response.data;
  },

  deleteConsulting: async (token, consultingId) => {
    const response = await axios.delete(`${API}/superadmin/consultings/${consultingId}`, {
      headers: { 'X-Super-Admin-Token': token }
    });
    return response.data;
  }
};
